import { useEffect, useId, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowRight, X } from "lucide-react";
import { scrollToId } from "@/lib/scroll";

export default function ExitIntent() {
    const location = useLocation();
    const navigate = useNavigate();
    const titleId = useId();
    const [open, setOpen] = useState(false);
    const onWebsites = location.pathname === "/websites";
    const shown = useRef(0);
    const openRef = useRef(false);
    openRef.current = open;

    useEffect(() => {
        if (window.matchMedia("(hover: none), (pointer: coarse)").matches) return undefined;

        let armed = false;
        const armTimer = window.setTimeout(() => {
            armed = true;
        }, 1600);

        const show = () => {
            if (!armed || openRef.current || shown.current >= 2) return;
            shown.current += 1;
            openRef.current = true;
            setOpen(true);
        };

        const onOut = (event) => {
            if (event.clientY > 0) return;
            if (event.relatedTarget || event.toElement) return;
            show();
        };

        document.documentElement.addEventListener("mouseout", onOut);
        return () => {
            window.clearTimeout(armTimer);
            document.documentElement.removeEventListener("mouseout", onOut);
        };
    }, []);

    useEffect(() => {
        if (!open) return undefined;
        const onKey = (event) => {
            if (event.key === "Escape") setOpen(false);
        };
        document.addEventListener("keydown", onKey);
        const previous = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.removeEventListener("keydown", onKey);
            document.body.style.overflow = previous;
        };
    }, [open]);

    const close = () => setOpen(false);

    const book = () => {
        setOpen(false);
        if (location.pathname === "/") {
            scrollToId("#contact");
            return;
        }
        navigate("/#contact");
    };

    const smallSite = () => {
        setOpen(false);
        if (onWebsites) {
            scrollToId("#get-started");
            return;
        }
        navigate("/websites#get-started");
    };

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-[120] flex items-center justify-center px-4" data-testid="exit-intent">
            <button
                type="button"
                aria-label="Close"
                className="absolute inset-0 bg-[#0a0a0b]/45"
                onClick={close}
            />
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                className="relative w-full max-w-md rounded-3xl bg-white px-7 py-8 shadow-[0_24px_80px_rgba(10,10,11,0.18)] sm:px-8"
            >
                <button
                    type="button"
                    onClick={close}
                    aria-label="Close"
                    className="absolute right-4 top-4 inline-flex h-9 w-9 items-center justify-center rounded-full text-[#5b5b66] hover:bg-[#f6f7fb] hover:text-[#0a0a0b]"
                >
                    <X className="h-4 w-4" />
                </button>
                <p className="font-display text-xs font-bold tracking-[0.04em] text-[#2B50FF]">Before you go!</p>
                <h2 id={titleId} className="mt-3 max-w-[16ch] font-display text-3xl font-extrabold leading-tight tracking-tight text-[#0a0a0b]">
                    Help for any budget.
                </h2>
                <p className="mt-3 text-sm leading-relaxed text-[#5b5b66]">
                    Thirty minutes, no fee. You leave with the scope and the price — hire us or don’t.
                </p>
                <ul className="mt-5 space-y-2 text-sm text-[#0a0a0b]">
                    <li className="flex gap-2">
                        <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#2B50FF]" />
                        A fixed package, priced before anyone builds
                    </li>
                </ul>
                <button
                    type="button"
                    data-testid="exit-intent-cta"
                    onClick={book}
                    className="group mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-[#0a0a0b] px-5 py-3 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5"
                >
                    Book a free consult
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </button>
                <button
                    type="button"
                    onClick={smallSite}
                    className="mt-3 block text-sm font-semibold text-[#0a0a0b] underline decoration-black/30 underline-offset-4 hover:text-[var(--brand)]"
                >
                    Or start with a $199 website
                </button>
            </div>
        </div>
    );
}
